import React, { useCallback, useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import api, { formatApiError } from "../../lib/api";
import { Save, Plus, Trash2, Radio, Users, PlayCircle } from "lucide-react";

/**
 * AdminHeroPulse — Super-Admin control for the homepage Platform Pulse.
 *
 * Ops can:
 *   1. Edit the 4 headline stats (Companies Evaluated, Institutions on
 *      File, Term Sheets Facilitated, Median Trust Score) → these are
 *      the marketing numbers, decoupled from the raw Mongo counts.
 *   2. Choose auto (pull top 3 approved startups) or manual (curated
 *      list) mode for featured opportunities.
 *   3. Add/edit/remove featured tiles, toggling `show` per row so
 *      individual entries can be hidden without deleting them.
 */
export default function AdminHeroPulse() {
  const [pulse, setPulse] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/public/hero-pulse");
      setPulse(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!pulse) {
    return (
      <DashboardShell>
        <div className="p-6 text-sm text-ink-muted">Loading Hero Pulse…</div>
      </DashboardShell>
    );
  }

  const setStat = (k, v) => setPulse((p) => ({ ...p, stats: { ...p.stats, [k]: Number(v) || 0 } }));
  const setLabel = (k, v) => setPulse((p) => ({ ...p, labels: { ...p.labels, [k]: v } }));
  const setChip = (k, v) => setPulse((p) => ({ ...p, intent_chips: { ...(p.intent_chips || {}), [k]: v } }));
  const setMode = (m) => setPulse((p) => ({ ...p, featured_mode: m }));
  // Two-surface video CMS (home + money). Patch is per-field so we never
  // clobber sibling values while typing.
  const setVideoField = (surface, field, value) => setPulse((p) => ({
    ...p,
    videos: {
      ...(p.videos || {}),
      [surface]: { ...((p.videos || {})[surface] || {}), [field]: value },
    },
  }));

  const addFeatured = () => setPulse((p) => ({
    ...p,
    featured: [
      ...(p.featured || []),
      { startup_name: "Confidential · New Sector", sector: "Sector", business_stage: "Series A", funding_ask: 100000000, trust_score: 80, show: true },
    ],
  }));
  const updateFeatured = (i, patch) => setPulse((p) => ({
    ...p,
    featured: p.featured.map((f, idx) => (idx === i ? { ...f, ...patch } : f)),
  }));
  const removeFeatured = (i) => setPulse((p) => ({
    ...p,
    featured: p.featured.filter((_, idx) => idx !== i),
  }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/hero-pulse", {
        labels: pulse.labels,
        stats: pulse.stats,
        intent_chips: pulse.intent_chips,
        videos: pulse.videos,
        featured_mode: pulse.featured_mode,
        featured: pulse.featured,
      });
      toast.success("Hero Pulse saved.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const S = pulse.stats || {};
  const L = pulse.labels || {};
  const C = pulse.intent_chips || {};

  return (
    <DashboardShell>
      <div className="space-y-6" data-testid="admin-hero-pulse">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">HOMEPAGE · MARKETING NUMBERS</div>
            <h1 className="font-display text-3xl text-ink mt-1 flex items-center gap-2"><Radio size={20} className="text-[#8A6600]" /> Hero Pulse Control</h1>
            <p className="text-sm text-ink-soft mt-1 max-w-2xl">
              What visitors see under the hero on the homepage. Numbers here override the raw Mongo counts; the featured
              list drives the &quot;Confidential opportunities&quot; ticker below the stat tiles.
            </p>
          </div>
          <button
            onClick={save}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-navy text-cream text-sm font-heading font-semibold hover:bg-[#256E58] disabled:opacity-60"
            data-testid="admin-hero-pulse-save"
          >
            <Save size={14} /> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>

        {/* Stats block */}
        <div className="border border-ink/10 rounded-xl bg-white p-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-3">Headline stats · edited freely</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["companies_evaluated", "institutions_on_file", "term_sheets", "median_trust"].map((k) => (
              <div key={k} className="border border-ink/10 rounded-lg p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Label</div>
                <input
                  value={L[k] || ""}
                  onChange={(e) => setLabel(k, e.target.value)}
                  className="w-full text-sm border border-ink/15 rounded px-2 py-1.5 mb-2 focus:outline-none focus:border-navy"
                  data-testid={`hp-label-${k}`}
                />
                <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Value</div>
                <input
                  type="number" min={0}
                  value={S[k] ?? 0}
                  onChange={(e) => setStat(k, e.target.value)}
                  className="w-full text-lg font-display border border-ink/15 rounded px-2 py-1.5 focus:outline-none focus:border-navy"
                  data-testid={`hp-value-${k}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Intent Picker live chips */}
        <div className="border border-ink/10 rounded-xl bg-white p-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-3">
            Intent Picker chips · shown under each homepage card
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { k: "founders",  label: "Raise Funds card (For Founders)" },
              { k: "investors", label: "Invest in Startups card (For Investors)" },
              { k: "debt",      label: "Access VenturaZ Money card" },
            ].map((row) => (
              <div key={row.k} className="border border-ink/10 rounded-lg p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">{row.label}</div>
                <input
                  value={C[row.k] || ""}
                  onChange={(e) => setChip(row.k, e.target.value)}
                  placeholder="e.g. 214 founders raising · ₹1,240 Cr live"
                  className="w-full text-sm border border-ink/15 rounded px-2 py-1.5 focus:outline-none focus:border-navy"
                  data-testid={`hp-chip-${row.k}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Overview Videos — CMS-driven */}
        <div className="border border-ink/10 rounded-xl bg-white p-5" data-testid="hp-videos-block">
          <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1 flex items-center gap-1.5">
                <PlayCircle size={12} className="text-[#8A6600]" /> Overview videos · &quot;How it works · 90s&quot;
              </div>
              <div className="text-sm text-ink-soft max-w-3xl">
                URLs render on the homepage hero pill + full section (equity)
                and the /money hero pill + full section (debt). Paste a
                YouTube / Vimeo / Loom watch link or a direct .mp4 URL —
                the player auto-detects. Leave blank to fall back to the
                animated &quot;Coming shortly&quot; placeholder.
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: "home",  label: "Homepage · /" },
              { key: "money", label: "VenturaZ Money · /money" },
            ].map((surface) => {
              const v = (pulse.videos || {})[surface.key] || {};
              return (
                <div key={surface.key} className="border border-ink/10 rounded-lg p-4" data-testid={`hp-video-${surface.key}`}>
                  <div className="flex items-center justify-between mb-3 gap-2">
                    <div className="text-[11px] font-mono uppercase tracking-widest text-ink font-semibold">
                      {surface.label}
                    </div>
                    <div className="flex items-center gap-2">
                      <EnabledSwitch
                        checked={v.enabled !== false}
                        onChange={(next) => setVideoField(surface.key, "enabled", next)}
                        testId={`hp-video-${surface.key}-enabled`}
                      />
                      {v.url ? (
                        <a
                          href={v.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] font-mono uppercase tracking-widest text-[#8A6600] hover:text-[#5A4400] inline-flex items-center gap-1"
                          data-testid={`hp-video-${surface.key}-open`}
                        >
                          Open source ↗
                        </a>
                      ) : (
                        <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Placeholder mode</span>
                      )}
                    </div>
                  </div>
                  {v.enabled === false && (
                    <div className="mb-3 text-[11px] font-mono uppercase tracking-widest text-rose-700 border border-rose-300 bg-rose-50 rounded px-2 py-1.5 inline-block" data-testid={`hp-video-${surface.key}-hidden-tag`}>
                      Hidden on public site
                    </div>
                  )}
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Video URL</label>
                  <input
                    value={v.url || ""}
                    onChange={(e) => setVideoField(surface.key, "url", e.target.value)}
                    placeholder="https://youtu.be/… or https://cdn.example.com/venturaz.mp4"
                    className="w-full text-sm border border-ink/15 rounded px-2 py-1.5 mb-3 focus:outline-none focus:border-navy"
                    data-testid={`hp-video-${surface.key}-url`}
                  />
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="col-span-2">
                      <label className="block text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Title</label>
                      <input
                        value={v.title || ""}
                        onChange={(e) => setVideoField(surface.key, "title", e.target.value)}
                        placeholder="How VenturaZ works — in 90 seconds"
                        className="w-full text-sm border border-ink/15 rounded px-2 py-1.5 focus:outline-none focus:border-navy"
                        data-testid={`hp-video-${surface.key}-title`}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Duration</label>
                      <input
                        value={v.duration || ""}
                        onChange={(e) => setVideoField(surface.key, "duration", e.target.value)}
                        placeholder="1:32"
                        className="w-full text-sm border border-ink/15 rounded px-2 py-1.5 focus:outline-none focus:border-navy"
                        data-testid={`hp-video-${surface.key}-duration`}
                      />
                    </div>
                  </div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Poster description</label>
                  <textarea
                    value={v.description || ""}
                    onChange={(e) => setVideoField(surface.key, "description", e.target.value)}
                    rows={2}
                    placeholder="Shown under the title on the banner poster."
                    className="w-full text-sm border border-ink/15 rounded px-2 py-1.5 focus:outline-none focus:border-navy resize-none"
                    data-testid={`hp-video-${surface.key}-description`}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <div className="border border-ink/10 rounded-xl bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Featured opportunities</div>
              <div className="text-sm text-ink">
                {pulse.featured_mode === "auto"
                  ? "Auto mode — top 3 approved startups render as Confidential · <sector>."
                  : "Manual mode — the list below drives what the ticker shows."}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ModeToggle mode={pulse.featured_mode} setMode={setMode} />
              {pulse.featured_mode !== "auto" && (
                <button
                  onClick={addFeatured}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-heading border border-ink/15 rounded hover:border-navy"
                  data-testid="hp-add-featured"
                >
                  <Plus size={12} /> Add row
                </button>
              )}
            </div>
          </div>

          {pulse.featured_mode === "auto" ? (
            <div className="p-4 border border-dashed border-ink/15 rounded text-sm text-ink-soft flex items-center gap-2">
              <Users size={14} /> Feed pulled live from `startups` where status ∈ (Approved, Investor Visible).
            </div>
          ) : (
            <div className="space-y-2" data-testid="hp-featured-list">
              {(pulse.featured || []).length === 0 && (
                <div className="text-sm text-ink-muted italic">No featured tiles yet. Click &quot;Add row&quot; above.</div>
              )}
              {(pulse.featured || []).map((f, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center border border-ink/10 rounded p-2" data-testid={`hp-featured-row-${i}`}>
                  <input
                    value={f.startup_name || ""}
                    onChange={(e) => updateFeatured(i, { startup_name: e.target.value })}
                    placeholder="Confidential · Fintech"
                    className="col-span-3 text-sm border border-ink/15 rounded px-2 py-1.5"
                    data-testid={`hp-feat-name-${i}`}
                  />
                  <input
                    value={f.sector || ""}
                    onChange={(e) => updateFeatured(i, { sector: e.target.value })}
                    placeholder="Sector"
                    className="col-span-2 text-sm border border-ink/15 rounded px-2 py-1.5"
                  />
                  <input
                    value={f.business_stage || ""}
                    onChange={(e) => updateFeatured(i, { business_stage: e.target.value })}
                    placeholder="Series A"
                    className="col-span-2 text-sm border border-ink/15 rounded px-2 py-1.5"
                  />
                  <input
                    type="number" min={0}
                    value={f.funding_ask ?? ""}
                    onChange={(e) => updateFeatured(i, { funding_ask: Number(e.target.value) || null })}
                    placeholder="Ask (₹)"
                    className="col-span-2 text-sm border border-ink/15 rounded px-2 py-1.5"
                  />
                  <input
                    type="number" min={0} max={100}
                    value={f.trust_score ?? ""}
                    onChange={(e) => updateFeatured(i, { trust_score: Number(e.target.value) || null })}
                    placeholder="Trust"
                    className="col-span-1 text-sm border border-ink/15 rounded px-2 py-1.5"
                  />
                  <label className="col-span-1 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-ink-muted">
                    <input
                      type="checkbox"
                      checked={f.show !== false}
                      onChange={(e) => updateFeatured(i, { show: e.target.checked })}
                      className="accent-navy"
                      data-testid={`hp-feat-show-${i}`}
                    />
                    Show
                  </label>
                  <button
                    onClick={() => removeFeatured(i)}
                    className="col-span-1 justify-self-end text-ink-muted hover:text-red-600"
                    data-testid={`hp-feat-remove-${i}`}
                    title="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Toaster richColors position="top-right" />
    </DashboardShell>
  );
}

function ModeToggle({ mode, setMode }) {
  return (
    <div className="inline-flex rounded border border-ink/15 overflow-hidden">
      {[
        { k: "auto", l: "Auto" },
        { k: "manual", l: "Manual" },
      ].map((o) => (
        <button
          key={o.k}
          onClick={() => setMode(o.k)}
          className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest ${mode === o.k ? "bg-navy text-cream" : "bg-white text-ink hover:bg-navy/[0.03]"}`}
          data-testid={`hp-mode-${o.k}`}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------
// EnabledSwitch — compact iOS-style toggle used in the Video CMS
// cards. Off state hides the whole surface pill + section on the
// public site without deleting the URL / copy.
// ------------------------------------------------------------------
function EnabledSwitch({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex items-center h-5 w-9 rounded-full transition-colors ${
        checked ? "bg-emerald-600" : "bg-ink/25"
      }`}
      data-testid={testId}
      title={checked ? "Enabled · shown on the public site" : "Disabled · hidden from the public site"}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
      <span className="sr-only">{checked ? "Enabled" : "Disabled"}</span>
    </button>
  );
}
